import { useQueryClient } from '@tanstack/react-query';
import { Button, Form, Row, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import type { FieldErrors } from 'react-hook-form';
import _ from 'lodash';

import useForm, { FormValue } from './useForm';
import { AxiosResponse } from 'axios';
import { I18n } from 'src/libs/i18n';

import { productFormat } from 'src/utils/product';
import LocalStorage from 'src/utils/LocalStorage';
import {
  checkIsProductFinished,
  convertFinishedSize,
  findOriginSizeByApiList,
  getProductName,
  checkChangeLanguage,
} from 'src/utils/helper';
import { getApiSeqnoFromToleranceCodes } from 'src/utils/tolerance';
import CustomerInfo from './CustomerInfo';
import MainTable from './MainTable';
import UploadCSVModal from './UploadCSVModal';
import quotation from 'src/repositories/quotation';
import { formatSizes } from 'src/utils/quotation';
import {
  FINISHED_PRODUCT_PARAMS,
  SCREEN_ID,
  STATUS_CODE,
} from 'src/constants/app';
import { API_KEY } from 'src/constants/apiKey';
import { MAX_TABLE_ITEMS, NUMBER_OF_INPUT_DIMENSIONS } from './constants';
import { usePriceQuotes } from 'src/queries/usePriceQuotes';
import usePriceFractions from 'src/queries/usePriceFractions';
import history from 'src/libs/history';
import ErrorMessage from './ErrorMessage';
import CalculateResultTable from './CalculateResultTable';
import LoadingOverlay from 'src/components/applications/LoadingOverlay';
import useAccountSetting from 'src/queries/useAccountSetting';
import useQueryUrl from 'src/hooks/useQueryUrl';

const MultipleQuotes: React.FC = () => {
  const [estimateErrors, setEstimateErrors] = useState<string[]>([]);
  const [stockError, setStockError] = useState('');
  const [dataReEstimate, setDataReEstimate] = useState<any>([
    { id: uuidv4(), isCreate: true },
  ]);
  const [lang, setLang] = useState(LocalStorage.lang);
  const [isAdding, setIsAdding] = useState(false);
  const initialSetRef = useRef(false);

  const apiPathPriceApiList = '/price/api-list';

  const { t } = useTranslation();
  const client = useQueryClient();
  const params = useQueryUrl<SearchParams.MultipleQuotesSearch>();
  const { pathname } = history.location;

  const {
    quotations,
    errorsWhenReQuoting,
    accountInfo,
    estimatePrices,
    exportPDF,
    isLoading,
  } = usePriceQuotes({});
  const { queryFractions } = usePriceFractions();
  const { queryAccount } = useAccountSetting({});

  const { formInfo } = useForm();

  const { handleSubmit, setValue, clearErrors, getValues, setError } = formInfo;

  const accountSetting = queryAccount?.data?.data?.item;
  const estimatePriceData = estimatePrices.data?.data?.quotations;
  const preOrderDetail = {
    orderClosingTime: estimatePrices.data?.data?.eigyouJikanTo,
    unchinSettei: estimatePrices.data?.data?.unchinSettei,
    kingakuHyoji: estimatePrices.data?.data?.kingakuHyoji,
  };
  const fractionsData = queryFractions?.data?.data?.item?.fractions;
  const prevLang = useRef(lang);

  useEffect(() => {
    setLang(LocalStorage.lang);
  }, [LocalStorage.lang]);

  useEffect(() => {
    if (quotations && !initialSetRef.current) {
      const errorsConvert = errorsWhenReQuoting?.map((item) => {
        return { errorCode: item };
      });
      const allErrorCodes = handleSetErrorCode(errorsConvert);
      // Set estimateErrors state to unique errorCodes
      setEstimateErrors(allErrorCodes);

      quotations?.length > 0 && setDataReEstimate(quotations);
      initialSetRef.current = true;
    }
  }, [quotations]);

  useEffect(() => {
    if (!params?.webMitrNos) {
      handleResetData();
    }
  }, [params?.webMitrNos]);

  useEffect(() => {
    // clear data when change language
    if (checkChangeLanguage(prevLang?.current, lang)) {
      handleResetData();
      history.replace(pathname);
    }
  }, [lang]);

  // Arrow function to merge item_1 and corresponding item_2 as a property "detail"
  const mergeItemsWithDetail = (item1Arr, item2Arr) => {
    return item1Arr.map((item, idx) => ({
      ...item,
      detail: !item2Arr[idx]?.errorCode ? item2Arr[idx] : null, // Attach corresponding item_2, or null if not exists
    }));
  };

  // Check for machining range limit errors.
  const checkErrorParamsProcessingScope = (
    index,
    params,
    sizeItem,
    showProcessingRange,
  ) => {
    const parts = sizeItem?.snpoGuide.split('x');
    const snpoSize = sizeItem?.snpoSize;

    params?.forEach((key) => {
      switch (Number(key)) {
        case snpoSize - 1:
          return setError(`priceQuotes.${index}.product_thickness`, {
            type: 'custom',
            message: t('message.E000091', {
              0: `${parts[snpoSize - 1]} (${snpoSize})`,
              1: showProcessingRange[0],
            }),
          });
        case snpoSize:
          return setError(`priceQuotes.${index}.product_width`, {
            type: 'custom',
            message: t('message.E000091', {
              0: `${parts[snpoSize]} (${snpoSize + 1})`,
              1: showProcessingRange[1],
            }),
          });
        case snpoSize + 1:
          return setError(`priceQuotes.${index}.product_length`, {
            type: 'custom',
            message: t('message.E000091', {
              0: `${parts[snpoSize + 1]} (${snpoSize + 2})`,
              1: showProcessingRange[2],
            }),
          });
        case snpoSize + 2:
          return setError(`priceQuotes.${index}.dimension_4`, {
            type: 'custom',
            message: t('message.E000091', {
              0: `${parts[snpoSize + 2]} (${snpoSize + 3})`,
              1: showProcessingRange[3],
            }),
          });
        case snpoSize + 3:
          return setError(`priceQuotes.${index}.dimension_5`, {
            type: 'custom',
            message: t('message.E000091', {
              0: `${parts[snpoSize + 3]} (${snpoSize + 4})`,
              1: showProcessingRange[4],
            }),
          });
        case snpoSize + 4:
          return setError(`priceQuotes.${index}.dimension_6`, {
            type: 'custom',
            message: t('message.E000091', {
              0: `${parts[snpoSize + 4]} (${snpoSize + 5})`,
              1: showProcessingRange[5],
            }),
          });
      }
    });
  };

  // list errorCode
  const handleSetErrorCode = (estimatePriceData) => {
    const errorMessages: string[] = [];

    estimatePriceData?.forEach((item, index) => {
      const errorCode = item?.errorCode;
      if (!errorCode) return;

      // ESTIMATE_PRICE_ERROR: push fixed code E000079
      if (errorCode === STATUS_CODE.ESTIMATE_PRICE_ERROR) {
        errorMessages.push(t('message.E000079'));
        return;
      }

      // ESTIMATE_STOCK_ERROR: push fixed stock error message
      if (errorCode === STATUS_CODE.ESTIMATE_STOCK_ERROR) {
        errorMessages.push(t('message.E000048'));
        return;
      }

      if (errorCode === STATUS_CODE.PRODUCT_NOT_FOUND_ERROR) {
        errorMessages.push(t('message.E000106'));
        return;
      }

      if (errorCode === STATUS_CODE.WRONG_FORMAT_SIZES) {
        const sizeItemError = getValues(`priceQuotes.${index}.sizeItem`) || '';
        errorMessages.push(t('message.E000012', { 0: sizeItemError?.head }));
        return;
      }

      if (errorCode === STATUS_CODE.OUT_OF_MACHINING_SIZE_RANGE_ERROR) {
        const sizeItem = getValues(`priceQuotes.${index}.sizeItem`);
        const params = item?.errorData?.params;
        const showProcessingRange =
          getValues(`priceQuotes.${index}.showProcessingRange`) || [];
        checkErrorParamsProcessingScope(
          index,
          params,
          sizeItem,
          showProcessingRange,
        );
      }

      // Comma separated multiple error codes
      if (errorCode.includes(',')) {
        const errors = errorCode
          .split(',')
          .map((code) =>
            code >= '001' && code <= '025' ? I18n.t(`message.${code}`) : '',
          )
          .filter((msg) => msg);

        // If nothing matched, push fallback error
        if (errors.length === 0) {
          errors.push(I18n.t('message.E000079'));
        }
        errorMessages.push(...errors);
        return;
      }

      // Range 001–025
      if (errorCode >= '001' && errorCode <= '025') {
        errorMessages.push(I18n.t(`message.${errorCode}`));
        return;
      }

      // Fallback: unknown code, use E000079
      errorMessages.push(t('message.E000079'));
    });

    // Remove duplicates and return
    return Array.from(new Set(errorMessages));
  };

  useEffect(() => {
    if (estimatePriceData?.length > 0) {
      const allErrorCodes = handleSetErrorCode(estimatePriceData);
      // Set estimateErrors state to unique errorCodes
      setEstimateErrors(allErrorCodes);

      const result = mergeItemsWithDetail(dataReEstimate, estimatePriceData);
      setDataReEstimate(result);
    }
  }, [estimatePriceData]);

  const handleAddData = () => {
    setDataReEstimate((prev) => [...prev, { id: uuidv4(), isCreate: true }]);
    setIsAdding(true);
  };

  // Refactor handleDelete to work by index to stay in sync with priceQuotes/useFieldArray
  const handleDelete = (index: number) => {
    setDataReEstimate((prev) => {
      if (index < 0) return prev;
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });

    if (dataReEstimate?.length === 0) {
      setEstimateErrors([]);
    }
  };

  const handleFormError = (errors: FieldErrors<FormValue>) => {
    console.log('formErrors:', errors);
  };

  const handleSwapWidthAndLength = (
    firstDimension,
    secondDimension,
    isSwappedS2AndS3,
    disableFractionS2AndS3,
    index,
    manufacturingSizes,
    val,
  ) => {
    if (
      manufacturingSizes?.swapS2AndS3 &&
      firstDimension &&
      secondDimension &&
      Number(firstDimension) > Number(secondDimension)
    ) {
      [firstDimension, secondDimension] = [secondDimension, firstDimension];

      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_3
      ) {
        setValue(`priceQuotes.${index}.product_width`, firstDimension);
        setValue(`priceQuotes.${index}.product_length`, secondDimension);
        if (
          val.decimalInputSizeFlags.isSnpo2BunsuHyoji &&
          val.decimalInputSizeFlags.isSnpo3BunsuHyoji
        ) {
          isSwappedS2AndS3 = true;
          setValue(`priceQuotes.${index}.isSwapDimension`, true);
        } else {
          disableFractionS2AndS3 = true;
        }
      }
      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_4
      ) {
        setValue(`priceQuotes.${index}.product_length`, firstDimension);
        setValue(`priceQuotes.${index}.dimension_4`, secondDimension);
        if (
          val.decimalInputSizeFlags.isSnpo3BunsuHyoji &&
          val.decimalInputSizeFlags.isSnpo4BunsuHyoji
        ) {
          isSwappedS2AndS3 = true;
          setValue(`priceQuotes.${index}.isSwapDimension`, true);
        } else {
          disableFractionS2AndS3 = true;
        }
      }
      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_5
      ) {
        setValue(`priceQuotes.${index}.dimension_4`, firstDimension);
        setValue(`priceQuotes.${index}.dimension_5`, secondDimension);
        if (
          val.decimalInputSizeFlags.isSnpo4BunsuHyoji &&
          val.decimalInputSizeFlags.isSnpo5BunsuHyoji
        ) {
          isSwappedS2AndS3 = true;
          setValue(`priceQuotes.${index}.isSwapDimension`, true);
        } else {
          disableFractionS2AndS3 = true;
        }
      }
      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_6
      ) {
        setValue(`priceQuotes.${index}.dimension_5`, firstDimension);
        setValue(`priceQuotes.${index}.dimension_6`, secondDimension);
        if (
          val.decimalInputSizeFlags.isSnpo5BunsuHyoji &&
          val.decimalInputSizeFlags.isSnpo6BunsuHyoji
        ) {
          isSwappedS2AndS3 = true;
          setValue(`priceQuotes.${index}.isSwapDimension`, true);
        } else {
          disableFractionS2AndS3 = true;
        }
      }
    }
    return [
      firstDimension,
      secondDimension,
      isSwappedS2AndS3,
      disableFractionS2AndS3,
    ];
  };

  /**
   * Wait for convert size API to complete before calculation
   * @returns Promise<void>
   */
  const waitForConvertSizeCompletion = async (): Promise<void> => {
    const MAX_RETRIES = 30; // Maximum 30 attempts (3 seconds)
    const POLLING_INTERVAL = 100; // Check every 100ms

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      const isMutatingConvertSize = client.isMutating({
        mutationKey: [API_KEY.CONVERT_SIZE],
      });

      if (isMutatingConvertSize === 0) {
        // API has completed, proceed with calculation
        break;
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
      retryCount++;
    }

    // If retry limit exceeded, log warning but continue (fail-safe)
    if (retryCount >= MAX_RETRIES) {
      console.warn(
        'Convert size API timeout - proceeding with calculation anyway',
      );
    }
  };

  const onHandleCalculate = async (formData: FormValue) => {
    // Wait for convert size API to complete before calculation
    await waitForConvertSizeCompletion();

    const priceApiListRes = await client.fetchQuery<
      AxiosResponse<Response.PriceApiList>
    >({
      queryKey: [apiPathPriceApiList],
      queryFn: quotation.getPriceApiList,
    });

    const priceApiList = priceApiListRes?.data?.items;
    const priceQuotes = formData?.priceQuotes || [];

    if (!priceQuotes.length) return;

    const priceQuotesFiltered = priceQuotes.map((val, index) => {
      const manufacturingSizes = val?.sizeItem;

      const product = val?.product || '';

      const listProductId = productFormat.splitProductId(product);

      // Determine preferred API seqno from tolerance codes
      const preferredApiSeqno = getApiSeqnoFromToleranceCodes(
        {
          thicknessCd: val?.machining_thickness,
          widthCd: val?.machining_width,
          lengthCd: val?.machining_length,
          outsideDiaCd: val?.machining_outside_diameter,
        },
        val?.toleranceOptions,
        priceApiList,
      );

      const sizes1Data =
        findOriginSizeByApiList(
          manufacturingSizes?.sizes,
          val?.product_thickness,
          priceApiList,
          listProductId,
          product,
          preferredApiSeqno,
        ) || val?.product_thickness;

      let firstDimension = val?.product_width;
      let secondDimension = val?.product_length;
      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_4
      ) {
        firstDimension = val?.product_length;
        secondDimension = val?.dimension_4;
      }
      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_5
      ) {
        firstDimension = val?.dimension_4;
        secondDimension = val?.dimension_5;
      }
      if (
        val.numberOfInputDimensions === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_6
      ) {
        firstDimension = val?.dimension_5;
        secondDimension = val?.dimension_6;
      }
      let isSwappedS2AndS3 = false;
      let disableFractionS2AndS3 = false;

      [
        firstDimension,
        secondDimension,
        isSwappedS2AndS3,
        disableFractionS2AndS3,
      ] = handleSwapWidthAndLength(
        firstDimension,
        secondDimension,
        isSwappedS2AndS3,
        disableFractionS2AndS3,
        index,
        manufacturingSizes,
        val,
      );

      let productWidthVal = val?.product_width;
      let productLengthVal = val?.product_length;
      let dimension4Val = val?.dimension_4;
      let dimension5Val = val?.dimension_5;
      let dimension6Val = val?.dimension_6;

      switch (val?.numberOfInputDimensions) {
        case NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_3:
          productWidthVal = firstDimension;
          productLengthVal = secondDimension;
          break;
        case NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_4:
          productLengthVal = firstDimension;
          dimension4Val = secondDimension;
          break;
        case NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_5:
          dimension4Val = firstDimension;
          dimension5Val = secondDimension;
          break;
        case NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_6:
          dimension5Val = firstDimension;
          dimension6Val = secondDimension;
          break;
        default:
          break;
      }

      // Get latest converted values from form instead of parameter val to avoid incorrect values during convert size API processing
      const latestConvertedThickness = getValues(
        `priceQuotes.${index}.converted_product_thickness`,
      );
      const latestConvertedWidth = getValues(
        `priceQuotes.${index}.converted_product_width`,
      );
      const latestConvertedLength = getValues(
        `priceQuotes.${index}.converted_product_length`,
      );
      const latestConvertedDimension4 = getValues(
        `priceQuotes.${index}.converted_dimension_4`,
      );
      const latestConvertedDimension5 = getValues(
        `priceQuotes.${index}.converted_dimension_5`,
      );
      const latestConvertedDimension6 = getValues(
        `priceQuotes.${index}.converted_dimension_6`,
      );

      const convertedSize = formatSizes({
        thickness: latestConvertedThickness || val?.product_thickness,
        width: latestConvertedWidth || productWidthVal,
        length: latestConvertedLength || productLengthVal,
        dimension4: latestConvertedDimension4 || dimension4Val,
        dimension5: latestConvertedDimension5 || dimension5Val,
        dimension6: latestConvertedDimension6 || dimension6Val,
      });
      const unitConvertedSize = formatSizes({
        thickness: manufacturingSizes?.unitSize1,
        width: manufacturingSizes?.size1Label && manufacturingSizes?.unitSize2,
        length: manufacturingSizes?.size2Label && manufacturingSizes?.unitSize3,
      });
      const inputSize = formatSizes({
        thickness: val?.product_thickness,
        width: productWidthVal,
        length: productLengthVal,
        dimension4: dimension4Val,
        dimension5: dimension5Val,
        dimension6: dimension6Val,
      });

      const rawQuote = {
        hinshL3Cd: val?.variety,
        keijoL3Cd: val?.shape,
        hakudoShhnCd: listProductId?.hakudoShhnCd,
        shhnCd: listProductId?.shhnCd,
        api1ShhnCd: listProductId?.api1ShhnCd,
        api2ShhnCd: listProductId?.api2ShhnCd,
        hakudoShhnm: val?.productItem
          ? getProductName(val?.productItem, true)
          : dataReEstimate[index]?.hakudoShhnm,
        setdnKakoCd: checkIsProductFinished(val?.variety)
          ? FINISHED_PRODUCT_PARAMS.DEFAULT_SETDN_KAKO_CD
          : val?.machiningItem?.value,
        setdnkako: checkIsProductFinished(val?.variety)
          ? FINISHED_PRODUCT_PARAMS.DEFAULT_SETDN_KAKO
          : val?.machiningItem?.label,
        snpoSize1: checkIsProductFinished(val?.variety)
          ? FINISHED_PRODUCT_PARAMS.DEFAULT_SNPO_SIZE1
          : latestConvertedThickness || val?.product_thickness,
        snpoSize2: latestConvertedWidth || productWidthVal,
        snpoSize3: latestConvertedLength || productLengthVal,
        snpoSize4: latestConvertedDimension4 || dimension4Val,
        snpoSize5: latestConvertedDimension5 || dimension5Val,
        snpoSize6: latestConvertedDimension6 || dimension6Val,
        finishingSize: checkIsProductFinished(val?.variety)
          ? FINISHED_PRODUCT_PARAMS.DEFAULT_FINISHING_SIZE
          : convertedSize,
        finishingSizeText: checkIsProductFinished(val?.variety)
          ? FINISHED_PRODUCT_PARAMS.DEFAULT_FINISHING_SIZE_TEXT
          : val?.sizeItem?.format,
        numberOfMember: parseInt(val?.number_product),
        thicknessToleranceLabel:
          val?.machining_thickness &&
          t('pricing_page.machining_tolerance.plate_thickness'),
        thicknessToleranceCd: val?.machining_thickness,
        widthToleranceLabel:
          val?.machining_width && t('pricing_page.machining_tolerance.width'),
        widthToleranceCd: val?.machining_width,
        lengthToleranceLabel:
          val?.machining_length && t('pricing_page.machining_tolerance.length'),
        lengthToleranceCd: val?.machining_length,
        outsideDiaToleranceLabel:
          val?.machining_outside_diameter &&
          t('pricing_page.machining_tolerance.outside_diameter'),
        outsideDiaToleranceCd: val?.machining_outside_diameter,
        insideDiaToleranceLabel:
          val?.machining_inside_diameter &&
          t('pricing_page.machining_tolerance.inside_diameter'),
        insideDiaToleranceCd: val?.machining_inside_diameter,
        millSheetCd: val?.mill_sheets,
        remarks: val?.order_notes,
        millingPatternCd: val?.chamferItem?.fraiseKakoPtn,
        millingPatternName: val?.chamferItem?.fraiseKakoPtnMeis,
        keijoInfo: {
          keijoL1Cd: manufacturingSizes?.keijoInfo?.keijoL1Cd,
          keijoL2Cd: manufacturingSizes?.keijoInfo?.keijoL2Cd,
          keijoL3Cd: manufacturingSizes?.keijoInfo?.keijoL3Cd,
        },
        sizes1: checkIsProductFinished(val?.variety)
          ? FINISHED_PRODUCT_PARAMS.DEFAULT_SIZE1
          : sizes1Data,
        sizes2: productWidthVal,
        sizes3: productLengthVal,
        sizes4: dimension4Val,
        sizes5: dimension5Val,
        sizes6: dimension6Val,
        zaikoTaniKbn: !manufacturingSizes?.sizeMustConvert
          ? convertFinishedSize({
              size: inputSize,
              unitSize: unitConvertedSize,
              manufacturingSizes: manufacturingSizes,
              product: product,
              method: String(val?.machiningItem?.value),
              inputValue: val,
            })
          : undefined,
        inputSunpo: manufacturingSizes?.sizeMustConvert
          ? convertFinishedSize({
              size: inputSize,
              unitSize: unitConvertedSize,
              manufacturingSizes: manufacturingSizes,
              product: product,
              method: String(val?.machiningItem?.value),
              inputValue: val,
            })
          : undefined,
        sunpoTaniKubun: manufacturingSizes?.originSize,
        motoTaniKubun: manufacturingSizes?.motoTaniKubun,
        convertFlg: manufacturingSizes?.sizeMustConvert,
        unitSizeConvert: unitConvertedSize,
        seihnJoho: val?.seihnJoho,
        regScreenID: SCREEN_ID.R1015,
        isSwappedS2AndS3,
        disableFractionS2AndS3,
      };

      return _.omitBy(
        rawQuote,
        (v) => _.isUndefined(v) || _.isNull(v) || v === '' || v === 0,
      );
    });

    const rawPayload = {
      deliveryDestinationCd: formData?.destination
        ? formData?.destination.slice(0, 3)
        : formData?.destinationItem?.rmOtokesaki?.otokesakiCd,
      quotations: priceQuotesFiltered,
    };

    const dataOmit = _.omitBy(
      rawPayload,
      (v, k) => _.isUndefined(v) || _.isNull(v) || v === '' || v === 0,
    );

    estimatePrices.mutate(dataOmit);
  };

  useEffect(() => {
    if (estimatePrices.isSuccess) {
      estimatePriceData.forEach((element, index) => {
        if (element?.clearTolerance) {
          setValue(`priceQuotes.${index}.machining_thickness`, '');
          setValue(`priceQuotes.${index}.machining_width`, '');
          setValue(`priceQuotes.${index}.machining_length`, '');
          setValue(`priceQuotes.${index}.machining_outside_diameter`, '');
        }
      });
    }
  }, [estimatePrices.isSuccess, estimatePriceData]);

  const handleResetData = () => {
    const { customer_name, destination, destinationItem } =
      formInfo.getValues();
    formInfo.reset({
      customer_name,
      destination,
      destinationItem,
      priceQuotes: [],
    });
    setDataReEstimate([{ id: uuidv4(), isCreate: true }]);
    setEstimateErrors([]);
  };

  const handleClearOrderDetail = () => {
    const clearedDetails = dataReEstimate.map((item) => ({
      ...item,
      detail: null,
    }));
    setDataReEstimate(clearedDetails);

    const currentQuotes = getValues('priceQuotes');
    const updatedQuotes = currentQuotes.map((q) => ({
      ...q,
      isSwapDimension: false,
    }));
    setValue('priceQuotes', updatedQuotes);
    setEstimateErrors([]);
  };

  const resetInputData = (index: number) => {
    setValue(`priceQuotes.${index}.product_thickness`, '');
    clearErrors(`priceQuotes.${index}.product_thickness`);
    setValue(`priceQuotes.${index}.product_width`, '');
    clearErrors(`priceQuotes.${index}.product_width`);
    setValue(`priceQuotes.${index}.product_length`, '');
    clearErrors(`priceQuotes.${index}.product_length`);
    setValue(`priceQuotes.${index}.dimension_4`, '');
    clearErrors(`priceQuotes.${index}.dimension_4`);
    setValue(`priceQuotes.${index}.dimension_5`, '');
    clearErrors(`priceQuotes.${index}.dimension_5`);
    setValue(`priceQuotes.${index}.dimension_6`, '');
    clearErrors(`priceQuotes.${index}.dimension_6`);
    setValue(`priceQuotes.${index}.number_product`, '');
    clearErrors(`priceQuotes.${index}.number_product`);
    setValue(`priceQuotes.${index}.seihnJoho`, undefined);
    clearErrors(`priceQuotes.${index}.seihnJoho`);
    setValue(`priceQuotes.${index}.machining_thickness`, '');
    setValue(`priceQuotes.${index}.machining_width`, '');
    setValue(`priceQuotes.${index}.machining_length`, '');
    setValue(`priceQuotes.${index}.machining_outside_diameter`, '');
    setValue(`priceQuotes.${index}.machining_inside_diameter`, '');
    setValue(`priceQuotes.${index}.mill_sheets`, '');
    setValue(`priceQuotes.${index}.order_notes`, '');
    setValue(`priceQuotes.${index}.toleranceOptions`, {});
  };

  const isDisabledButtonSubmit = (dataReEstimate: any[]) =>
    dataReEstimate?.length === 0 ||
    (dataReEstimate?.length === 1 && !_.get(dataReEstimate, '[0].product'));

  return (
    <div className="mb-20 mt-20 multiple_quotes">
      {isLoading && <LoadingOverlay />}
      <Row className="header">
        <Typography.Title level={1} className="title">
          {t('multiple_quotes_page.title')}
        </Typography.Title>
        <UploadCSVModal
          formInfo={formInfo}
          setDataReEstimate={setDataReEstimate}
          setEstimateErrors={setEstimateErrors}
        />
      </Row>
      {_.size(estimateErrors) > 0 &&
        estimateErrors?.map((error, index) => (
          <ErrorMessage key={index} content={error} />
        ))}
      <Form onFinish={handleSubmit(onHandleCalculate, handleFormError)}>
        <CustomerInfo
          formInfo={formInfo}
          accountInfo={accountInfo || accountSetting}
          prevLang={prevLang?.current}
          lang={lang}
        />

        <MainTable
          isAdding={isAdding}
          setIsAdding={setIsAdding}
          formInfo={formInfo}
          setEstimateErrors={setEstimateErrors}
          setStockError={setStockError}
          dataReEstimate={dataReEstimate}
          prevLang={prevLang?.current}
          lang={lang}
          resetInputData={resetInputData}
          estimateErrors={estimateErrors}
          stockError={stockError}
          handleDelete={handleDelete}
          setDataReEstimate={setDataReEstimate}
          fractionsData={fractionsData}
        />
        {dataReEstimate?.every((item) => !item?.detail) && (
          <div>
            <Button
              type="primary"
              className="btn-custom"
              style={{ minWidth: 187, marginTop: 20 }}
              onClick={handleAddData}
              disabled={dataReEstimate?.length === MAX_TABLE_ITEMS}
            >
              {t('multiple_quotes_page.add_line')}
            </Button>
            <Row className="mb-10 mt-20" align="middle" justify="center">
              <Button
                className="btn-custom"
                type="primary"
                htmlType="submit"
                style={{ minWidth: 170 }}
                disabled={isDisabledButtonSubmit(dataReEstimate)}
                // loading={estimatePrice.isLoading}
              >
                {t('multiple_quotes_page.request_quotation')}
              </Button>
            </Row>
          </div>
        )}
      </Form>

      {dataReEstimate?.some((item) => item?.detail) && (
        <div className="order-detail-wrap">
          <div style={{ marginTop: 20 }}>
            <Button
              className="btn-custom custom_btn_multiple_quotes btn_clear_all"
              type="primary"
              style={{ minWidth: 170, marginRight: 20 }}
              onClick={handleResetData}
            >
              {t('multiple_quotes_page.clear_all')}
            </Button>
            <Button
              className="btn-custom custom_btn_multiple_quotes btn_clear_result"
              type="primary"
              style={{ minWidth: 170 }}
              onClick={handleClearOrderDetail}
            >
              {t('multiple_quotes_page.clear_result')}
            </Button>
          </div>
          <CalculateResultTable
            preOrderData={estimatePriceData?.filter((item) => !item?.errorCode)}
            preOrderDetail={preOrderDetail}
            exportPDF={exportPDF}
          />
        </div>
      )}
    </div>
  );
};

export default MultipleQuotes;
